import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import FeedbackOverview from './FeedbackOverview';
import FeedbackSectionRow, {
  isFeedbackSectionCompleted,
} from './FeedbackSectionRow';
import FeedbackSectionSelected from './FeedbackSectionSelected';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { useClient } from 'shared/apollo';
import {
  AnswersFeedbacks,
  getAnswersFeedback,
  getCommentsByReportId,
} from './api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import {
  CommentsBySectionIdResponse,
  toDatabaseUserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { SectionFeedbackProps } from 'donor/portal/pages/FeedbackDashboard/Feedback/SectionFeedback';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  FeedbackBySections,
  FeedbackCommentBodyWithIndex,
  getFeedbackList,
  getPreviousAndNextFeedback,
  mapToSections,
  SelectedFeedback,
  AllVerificationsProgress,
  readyForSubmission,
  getProgress,
  allFeedbackSectionsWithCustomQuestionsAndAllocations,
} from './utils';
import {
  FeedbackCommentProps,
  FeedbackSection,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import SubmitReady from './SubmitReady';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { customQuestionsById } from 'admin/pages/ManageAccounts/CustomQuestions/hook';
import { FeedbackReportHandler } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';

const StyledDiv = styled.div`
  height: 100%;
  background-color: ${COLORS.whiteBase};
  .feedback-rows {
    margin-left: 35px;
    margin-right: 35px;
    margin-bottom: ${SPACERS.extraLarge};
    border-top: 1px solid ${COLORS.greyBase};
  }
  .buttons-wrapper {
    display: flex;
    justify-content: space-between;
    padding: 14px 27px;
    background-color: ${COLORS.grayDark};
    .left-button {
      color: ${COLORS.whiteBase};
      outline-color: ${COLORS.whiteBase};
      border-color: ${COLORS.whiteBase};
      &:hover {
        background-color: ${COLORS.grayDarkest};
      }
    }
    .right-button {
      color: inherit;
      background-color: ${COLORS.whiteBase};
      &:hover {
        background-color: ${COLORS.grayLight};
      }
    }
  }
`;

const styles = { flex: 1 };
const NPOFeedback: React.FC<SectionFeedbackProps & {
  feedbackReportRef: React.RefObject<FeedbackReportHandler>;
}> = ({
  reportId,
  report,
  programName,
  currentUserId,
  currentUserEmail,
  userName,
  userType,
  feedbackStatus,
  customQuestions: c,
  allFeedbackSections,
  className,
  feedbackReportRef,
  refreshFeedbackStatus,
}) => {
  const client = useClient();
  const { isAdmin, isDonor } = useUserActions();
  const [requestState, makeRequest] = useGraphQLRequest<
    CommentsBySectionIdResponse
  >();
  const [requestFeedbackState, makeFeedbackRequest] = useGraphQLRequest<
    AnswersFeedbacks
  >();
  const [selectedSection, setSelectedSection] = useState<
    SelectedFeedback | undefined
  >(undefined);
  const [allSections, setAllSections] = useState<FeedbackSection[]>([]);
  const [feedbackBySections, setFeedbackBySections] = useState<
    FeedbackBySections
  >({});
  const [feedbackArray, setFeedbackArray] = useState<SelectedFeedback[]>([]);
  const [progress, setProgress] = useState<AllVerificationsProgress>({});
  const [showEnd, setShowEnd] = useState<boolean>(false);

  const customQuestions = useMemo(() => customQuestionsById(c), [c]);

  useEffect(() => {
    makeFeedbackRequest(getAnswersFeedback(client!, reportId));
  }, [reportId, makeFeedbackRequest]);

  useEffect(() => {
    if (requestFeedbackState.type !== 'REQUEST_SUCCESS') return;
    setProgress(getProgress(requestFeedbackState.data.allAnswersFeedback));
  }, [requestFeedbackState]);

  const handleDeleteComment = useCallback(
    (selected: SelectedFeedback, index: number) => {
      setFeedbackBySections(s => {
        const newState = { ...s };
        const section = s[selected.section.sectionId];
        if (!section) return s;
        if (!selected.question) {
          section.overAllSection.splice(index, 1);
        } else {
          const question =
            section.feedbackByQuestionId[selected.question.questionId];
          if (!question) return s;
          question.splice(index, 1);
        }
        return newState;
      });
    },
    [setFeedbackBySections],
  );

  const handleUpdateComment = useCallback(
    (
      selected: SelectedFeedback,
      index: number,
      comment: FeedbackCommentProps,
    ) => {
      setFeedbackBySections(s => {
        const newState = { ...s };
        const section = s[selected.section.sectionId];
        if (!section) return s;
        if (!selected.question) {
          section.overAllSection[index].comment = comment.text;
        } else {
          const question =
            section.feedbackByQuestionId[selected.question.questionId];
          if (!question) return s;
          question[index].comment = comment.text;
        }
        return newState;
      });
    },
    [setFeedbackBySections],
  );

  const handleAddComment = useCallback(
    (selected: SelectedFeedback, comment: FeedbackCommentProps) => {
      setFeedbackBySections(s => {
        const newState = { ...s };
        const section = s[selected.section.sectionId];
        if (!section) return s;
        const c: FeedbackCommentBodyWithIndex = {
          index: 0,
          details: comment.details,
          comment: comment.text,
          commentId: comment.commentId,
          createdAt: comment.createdAt,
          questionId: selected.question?.questionId,
          questionText: selected.question?.questionText,
          reportId: reportId,
          sectionId: selected.section.sectionId,
          userEmail: currentUserEmail || '',
          userId: currentUserId ? parseInt(currentUserId) || -1 : -1,
          userName: userName,
          userType: toDatabaseUserType(userType),
        };
        if (!selected.question) {
          c.index = section.overAllSection.length;
          section.overAllSection.push(c);
        } else {
          const question =
            section.feedbackByQuestionId[selected.question.questionId];
          if (!question) return s;
          c.index = question.length;
          question.push(c);
        }
        return newState;
      });
    },
    [
      reportId,
      currentUserEmail,
      currentUserId,
      userName,
      userType,
      setFeedbackBySections,
    ],
  );

  useEffect(() => {
    makeRequest(getCommentsByReportId(client!, reportId));
  }, [reportId, makeRequest]);

  useEffect(() => {
    if (requestState.type !== 'REQUEST_SUCCESS') return;
    const { sections, feedbackBySections } = mapToSections(
      requestState.data.allNpoReportComments,
      allFeedbackSectionsWithCustomQuestionsAndAllocations,
    );
    setAllSections(sections);
    setFeedbackBySections(feedbackBySections);
    setFeedbackArray(
      getFeedbackList(sections, feedbackBySections, {
        question: undefined,
        section: sections[0],
      }).result,
    );
  }, [requestState, setAllSections, setFeedbackBySections, setFeedbackArray]);

  const submissionProgress = useMemo(
    () =>
      readyForSubmission(
        feedbackArray.length,
        userType,
        feedbackStatus,
        progress,
      ),
    [progress, feedbackArray.length, userType, feedbackStatus],
  );

  const handleFirstSection = useCallback(() => {
    const firstSection = allSections[0];
    const feedback = feedbackBySections[firstSection.sectionId];
    if (!feedback) return;
    const questionId =
      feedback.overAllSection.length === 0
        ? parseInt(Object.keys(feedback.feedbackByQuestionId)[0])
        : undefined;
    setSelectedSection({
      section: firstSection,
      question: questionId
        ? {
            questionId,
            questionText:
              feedback.feedbackByQuestionId[questionId][0].questionText ||
              `Unknown - ${questionId}`,
          }
        : undefined,
    });
  }, [allSections, setSelectedSection]);

  const handlePrevious = useCallback(() => {
    if (!selectedSection) return;
    const { previousFeedback } = getPreviousAndNextFeedback(
      selectedSection,
      feedbackBySections,
      allSections,
    );
    setSelectedSection(previousFeedback);
  }, [selectedSection, feedbackBySections, allSections, setSelectedSection]);

  const handleNext = useCallback(() => {
    if (!selectedSection) return;
    const { nextFeedback } = getPreviousAndNextFeedback(
      selectedSection,
      feedbackBySections,
      allSections,
    );

    if (
      nextFeedback === undefined &&
      (submissionProgress.ready || isAdmin || isDonor)
    ) {
      setShowEnd(true);
      return;
    }
    setSelectedSection(nextFeedback);
  }, [
    submissionProgress.completed,
    selectedSection,
    feedbackBySections,
    allSections,
    setShowEnd,
    setSelectedSection,
  ]);

  const selectedIndex = useMemo(
    () =>
      selectedSection
        ? selectedSection.question
          ? feedbackArray.findIndex(
              x =>
                x.question?.questionId === selectedSection.question?.questionId,
            )
          : feedbackArray.findIndex(
              x => x.section.sectionId === selectedSection.section.sectionId,
            )
        : undefined,
    [selectedSection, feedbackArray],
  );

  if (
    requestState.type === 'REQUEST_INIT' ||
    requestState.type === 'REQUEST_START' ||
    requestFeedbackState.type === 'REQUEST_INIT' ||
    requestFeedbackState.type === 'REQUEST_START'
  ) {
    return (
      <div style={styles}>
        <TrueImpactLoading />
      </div>
    );
  }

  if (
    requestFeedbackState.type === 'REQUEST_SUCCESS' &&
    requestFeedbackState.type === 'REQUEST_SUCCESS'
  ) {
    if (showEnd) {
      return (
        <SubmitReady
          userType={userType}
          isThirdPartyReport={report.cnOptedIn || false}
          total={feedbackArray.length}
          feedbackStatus={feedbackStatus}
          handleBack={() => {
            setSelectedSection(undefined);
            setShowEnd(false);
          }}
          isReady={submissionProgress.ready}
          reportId={reportId}
          progress={progress}
          className={className}
          refreshFeedbackStatus={refreshFeedbackStatus}
        />
      );
    }

    return (
      <StyledDiv className={className}>
        <FeedbackOverview
          handleStart={handleFirstSection}
          handleDone={() => setShowEnd(true)}
          feedbackStatus={feedbackStatus}
          userType={userType}
          showHeader={selectedSection === undefined}
          numberOfCompletedFeedback={submissionProgress.completed.length}
          totalFeedbacks={feedbackArray.length}
        />
        <div className="feedback-rows">
          {selectedSection === undefined ? (
            allSections.map(s => (
              <FeedbackSectionRow
                key={s.sectionId}
                section={s}
                progress={progress}
                programName={programName}
                customQuestions={customQuestions}
                feedbackStatus={feedbackStatus}
                feedbackBySections={feedbackBySections}
                setSelectedSection={setSelectedSection}
              />
            ))
          ) : (
            <FeedbackSectionSelected
              total={feedbackArray.length}
              selectedSection={selectedSection}
              feedbackSections={feedbackBySections}
              feedbackStatus={feedbackStatus}
              report={report}
              reportId={reportId}
              reportYear={report.year || undefined}
              currentUserId={currentUserId}
              currentUserEmail={currentUserEmail}
              userName={userName}
              userType={userType}
              programName={programName}
              reloadReportAnswers={
                feedbackReportRef.current?.reloadReportAnswers
              }
              isSectionCompleted={isFeedbackSectionCompleted(
                selectedSection.question?.questionId ||
                  selectedSection.section.sectionId,
                feedbackStatus,
                progress,
              )}
              selectedIndex={selectedIndex}
              handleNext={handleNext}
              setProgress={setProgress}
              handleDeleteComment={handleDeleteComment}
              handleUpdateComment={handleUpdateComment}
              handleAddComment={handleAddComment}
            />
          )}
        </div>
        {selectedSection !== undefined && (
          <div className="buttons-wrapper">
            <TrueImpactButton
              variant="secondary-large"
              onClick={handlePrevious}
              className="left-button"
            >
              Previous Feedback Item
            </TrueImpactButton>
            <TrueImpactButton onClick={handleNext} className="right-button">
              Next Feedback Item
            </TrueImpactButton>
          </div>
        )}
      </StyledDiv>
    );
  }
  return (
    <TrueImpactText variant="body-bold" color="critical" style={styles}>
      Unable to load the feedbacks
    </TrueImpactText>
  );
};

export default NPOFeedback;
